import { find, map, stubTrue } from "lodash/fp";

import { PARTICIPANT_ACCESS_OPTIONS } from "@app/analytics/constants/sharing";
import type { FeatureAccess } from "@app/analytics/providers/FeatureAccessContext";
import type { CalendarPermissionLevel } from "@app/analytics/types";
import {
  DEFAULT_MEETING_RECAP_EMAIL_DISTRIBUTION,
  DEFAULT_PRE_READ_EMAIL_DISTRIBUTION,
} from "@app/common/constants/settings";
import type {
  ParticipantAccessLevel,
  User,
  UserDistributionSetting,
  UserSettings,
} from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";
import * as UserUtils from "@app/common/utils/userUtils";

export interface Option {
  label: string;
  value: string;
  default?: boolean;
  setting?: keyof UserSettings;
}

interface CommonSetting {
  name: string;
  label: string;
  canEdit: (opts: { featureAccess: FeatureAccess; user: User }) => boolean;
  /* Description of the setting. Will appear as a "What's this?" button in the advanced settings modal */
  description?: string;
  disableBorder?: boolean;
  enabled: (user: User) => boolean;
  /* helper method for getting the value of the selected option based on the user/user settings */
  getOptionFromSettings: (settings: UserSettings) => string;
  /* helper method for determining the user settings PATCH value from the input value */
  getSettingsFromValue: (value: string, user: User) => Partial<UserSettings>;
  /* Tooltip that will appear for preview settings */
  tooltip?: string;
}

export interface SelectSetting
  extends Omit<CommonSetting, "getOptionFromSettings"> {
  type: "select";
  options: (user: User) => Option[];
  /* helper method for getting the value of the selected option based on the user/user settings */
  getOptionFromSettings: (settings: UserSettings, options: Option[]) => Option;
}

export interface ToggleSetting extends CommonSetting {
  type: "toggle";
}

export type Setting = SelectSetting | ToggleSetting;

const filterOptions = (options: Option[], allowedOptions?: string[]) => {
  if (!allowedOptions) return options;
  return options.filter((option) => allowedOptions.includes(option.value));
};

const DEFAULT_CALENDAR_PERMISSION_LEVEL_OPTIONS = [
  { value: "all", label: "All calendar events", default: true },
  {
    value: "hosting_only",
    label: "Calendar events I host",
  },
  {
    value: "invite_only",
    label: "Calendar events that Read is invited to",
  },
];

const DEFAULT_MEETING_TYPE_OPTIONS = [
  { value: "false", label: "Internal and external", default: true },
  { value: "true", label: "Internal only" },
];

const EMAIL_DISTRIBUTION_OPTIONS = [
  {
    value: "participants_with_access",
    label: "Participants with access",
    default: true,
  },
  { value: "me", label: "Only me" },
  { value: "no_one", label: "No one" },
];

const JOIN_PREFERENCE_SETTING: SelectSetting = {
  name: "calendarJoin",
  label: "Read Assistant will join:",
  type: "select",
  enabled: stubTrue,
  options: (user) =>
    filterOptions(
      DEFAULT_CALENDAR_PERMISSION_LEVEL_OPTIONS,
      user.editOptions?.calendarPermissionLevel
    ),
  getOptionFromSettings: (settings, options) => {
    const value = settings.calendarPermissionLevel ?? "all";
    return find({ value }, options)!;
  },
  getSettingsFromValue: (value) => ({
    calendarPermissionLevel: value as CalendarPermissionLevel,
  }),
  canEdit: stubTrue,
  tooltip:
    "This setting determines which meetings Read Assistant will automatically join. When Read joins a meeting, it will record and generate a meeting recap.",
  disableBorder: true,
};

const MEETING_TYPE_SETTING: SelectSetting = {
  name: "meetingType",
  label: "Where participants are:",
  type: "select",
  enabled: UserUtils.hasCompanyEmail,
  options: (user) =>
    filterOptions(
      DEFAULT_MEETING_TYPE_OPTIONS,
      map((v) => `${v}`, user?.editOptions?.calendarJoinInternalOnly)
    ),
  getOptionFromSettings: (settings, options) => {
    const value = settings.calendarOnlyJoinInternal ?? false;
    return find({ value: `${value}` }, options)!;
  },
  getSettingsFromValue: (value) => ({
    calendarOnlyJoinInternal: value === "true",
  }),
  canEdit: stubTrue,
};

const TRANSCRIPTION_OPT_IN_SETTING: ToggleSetting = {
  name: "transcription",
  label: "Meeting transcription",
  type: "toggle",
  enabled: stubTrue,
  canEdit: ({ featureAccess }) => featureAccess.canEditTranscriptionOptIn,
  getSettingsFromValue: (value) => ({ transcriptionOptIn: value === "true" }),
  getOptionFromSettings: (settings) =>
    settings.transcriptionOptIn ? "true" : "false",
};

const PLAYBACK_SETTING: ToggleSetting = {
  name: "playback",
  label: "Audio and video playback",
  type: "toggle",
  enabled: stubTrue,
  canEdit: ({ featureAccess }) => featureAccess.canEditStoreRecordingOptIn,
  getSettingsFromValue: (value) => ({ storeRecordingOptIn: value === "true" }),
  getOptionFromSettings: (settings) =>
    settings.storeRecordingOptIn ? "true" : "false",
};

const INTERNAL_PARTICIPANT_ACCESS_SETTING: SelectSetting = {
  name: "internalParticipantAccessLevel",
  label: "Internal participant report access",
  type: "select",
  enabled: UserUtils.hasCompanyEmail,
  options: () => PARTICIPANT_ACCESS_OPTIONS.internal,
  getOptionFromSettings: (settings) => {
    const value = settings.internalParticipantAccessLevel ?? "no_access";
    return find({ value }, PARTICIPANT_ACCESS_OPTIONS.internal) as Option;
  },
  getSettingsFromValue: (value) => ({
    internalParticipantAccessLevel: value as ParticipantAccessLevel,
  }),
  canEdit: ({ featureAccess }) =>
    featureAccess.canEditInternalParticipantAccessLevel,
  disableBorder: true,
};

const EXTERNAL_PARTICIPANT_ACCESS_SETTING: SelectSetting = {
  name: "externalParticipantAccessLevel",
  label: "External participant report access",
  type: "select",
  enabled: stubTrue,
  options: () => PARTICIPANT_ACCESS_OPTIONS.external,
  getOptionFromSettings: (settings) => {
    const value = settings.externalParticipantAccessLevel ?? "no_access";
    return find({ value }, PARTICIPANT_ACCESS_OPTIONS.external) as Option;
  },
  getSettingsFromValue: (value) => ({
    externalParticipantAccessLevel: value as ParticipantAccessLevel,
  }),
  canEdit: ({ featureAccess }) =>
    featureAccess.canEditExternalParticipantAccessLevel,
};

const MEETING_RECAP_EMAIL_DISTRIBUTION_SETTING: SelectSetting = {
  name: "emailMeetingRecaps",
  label: "Email meeting recaps to:",
  type: "select",
  enabled: stubTrue,
  options: () => EMAIL_DISTRIBUTION_OPTIONS,
  canEdit: ({ featureAccess }) => featureAccess.canEditSendMeetingRecap,
  getOptionFromSettings: (settings, options) => {
    const meetingRecapEmailDistributionSetting =
      UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
        settings,
        { category: "meeting_report", provider: "email" }
      );

    const value =
      (meetingRecapEmailDistributionSetting?.isEnabled
        ? meetingRecapEmailDistributionSetting.sendTo
        : "no_one") ?? "no_one";
    return find({ value }, options)!;
  },
  getSettingsFromValue: (value, user) => {
    const existingSetting =
      UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
        user,
        { category: "meeting_report", provider: "email" }
      ) ?? DEFAULT_MEETING_RECAP_EMAIL_DISTRIBUTION;

    const emailRecapDistributionSetting: UserDistributionSetting = {
      ...existingSetting,
      isEnabled: value !== "no_one",
      sendTo:
        value === "no_one"
          ? existingSetting.sendTo
          : (value as UserDistributionSetting["sendTo"]),
    };
    return { userDistributionSettings: [emailRecapDistributionSetting] };
  },
  description:
    "Meeting recaps include a summary of the meeting, topics, action items, key questions, transcript, highlights, and participant talk time. They may also include audio and video playback (depending on your plan).",
  tooltip:
    "This setting determines who will automatically receive a recap after a meeting ends. Participants with access refers to your internal and external participant access settings, which can be managed in advanced settings.",
};

const MEETING_PRE_READ_EMAIL_DISTRIBUTION_SETTING: SelectSetting = {
  name: "preReads",
  label: "Send Pre-Reads to:",
  type: "select",
  enabled: stubTrue,
  options: () => EMAIL_DISTRIBUTION_OPTIONS,
  canEdit: ({ featureAccess }) => featureAccess.canEditSendMeetingPreRead,
  getOptionFromSettings: (settings, options) => {
    const preReadEmailDistributionSetting =
      UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
        settings,
        { category: "meeting_pre_read", provider: "email" }
      );

    const value =
      (preReadEmailDistributionSetting?.isEnabled
        ? preReadEmailDistributionSetting.sendTo
        : "no_one") ?? "no_one";
    return find({ value }, options)!;
  },
  getSettingsFromValue: (value, user) => {
    const existingSetting =
      UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
        user,
        { category: "meeting_pre_read", provider: "email" }
      ) ?? DEFAULT_PRE_READ_EMAIL_DISTRIBUTION;

    const emailPreReadDistributionSetting: UserDistributionSetting = {
      ...existingSetting,
      isEnabled: value !== "no_one",
      sendTo:
        value === "no_one"
          ? existingSetting.sendTo
          : (value as UserDistributionSetting["sendTo"]),
    };
    return { userDistributionSettings: [emailPreReadDistributionSetting] };
  },
  description:
    "Pre-Reads can be sent before a recurring meeting with a recap of the previous meeting. It can only be sent to people with access to the previous report who are also invited to the upcoming meeting.",
};

const SETTINGS: readonly Setting[] = [
  JOIN_PREFERENCE_SETTING,
  MEETING_TYPE_SETTING,
  TRANSCRIPTION_OPT_IN_SETTING,
  PLAYBACK_SETTING,
  INTERNAL_PARTICIPANT_ACCESS_SETTING,
  EXTERNAL_PARTICIPANT_ACCESS_SETTING,
  MEETING_RECAP_EMAIL_DISTRIBUTION_SETTING,
  MEETING_PRE_READ_EMAIL_DISTRIBUTION_SETTING,
];

export const PREVIEW_SETTINGS: readonly Setting[] = [
  JOIN_PREFERENCE_SETTING,
  MEETING_RECAP_EMAIL_DISTRIBUTION_SETTING,
];

export default SETTINGS;
